import { PersonsFilter } from "@/components/common/section-components/filters/PersonsFilter";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { AppRoutes } from "@/constants/routes";
import {
  GetListadoPersonasProps,
  GetListadoPersonasResponse,
} from "@/services/persons.service";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { PersonaDetalle, personFilterSchema } from "./schema/person.schema";
import { PersonActions } from "@/components/common/section-components/action/PersonActions";
import { PersonHeaderElements } from "./schema/header-elements";
import { NoData } from "@/components/common/section-components/NoData";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import {
  clearFiltersHandler,
  fetchPersonsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { usePolling } from "@/hooks/usePolling";
export const PersonsPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loadingPersons, setLoadingPersons] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const [persons, setPersons] = useState<GetListadoPersonasResponse | null>({
    listado: [],
    totalPages: 1,
    totalRecords: 0,
  });

  useEffect(() => {
    setIsActive(true);
  }, []);
  const [searchParams] = useSearchParams();

  const fetchPersons = async (
    filters?: GetListadoPersonasProps,
    showLoad = true,
  ) => {
   await fetchPersonsHandler({
    setLoadingPersons,
    setPersons,
    navigate,
    fetchPermissions,
    showLoad,
    filters,
   })
  };

  const fetchWithParams = async (params: { dni: string }) => {
    setValue("dni", params?.dni);
    setFiltersApplied(true);
    await fetchPersons({
      Dni: params?.dni,
    });
  };

  useEffect(() => {
    if (isActive) {
      const statePage = location?.state?.page;
      if (statePage) {
        setPage(statePage);
      }

      const dni = searchParams.get("dni") ?? undefined;
      if (dni) {
        fetchWithParams({
          dni,
        });
      } else {
        fetchPersons({ "P.Pagina": statePage ?? 1 });
      }
    }
  }, [isActive]);

  usePolling({
    callback: () =>
      fetchPersons({ ...currentFilters, "P.Pagina": page }, false),
    delay: 10000,
    isActive,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const { control, handleSubmit, setValue, getValues } = useForm({
    resolver: zodResolver(personFilterSchema),
  });

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [page, setPage] = useState(1);

  const [currentFilters, setCurrentFilters] = useState<GetListadoPersonasProps>(
    {},
  );

  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < selection?.length;
  const handlePageChange = async (page: number) => {
    setPage(page);
    await fetchPersons({ ...currentFilters, "P.Pagina": page });
  };

  const { fetchPermissions } = useRoles();

  const clearFilters = async () => {
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
    });
  };

  const submitFilters = async (event: FieldValues) => {
    submitFiltersHandler({
      event,
      fetchPersons,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      setPage,
      navigate,
      fetchPermissions,
    });
  };

  const handlePersonDetail = (id: string) => {
    navigate(AppRoutes.PERSON_DETAIL.replace(":id", id), {
      state: { page },
    });
  };

  const getActionElements = (item: PersonaDetalle) => (
    <PersonActions item={item} handlePersonDetail={handlePersonDetail} />
  );

  const handleBackButton = () => {
    navigate(-1);
  };

  const handleAdd = () => {
    navigate(AppRoutes.PERSON_CREATE);
  };

  const { userPermissions } = useRoles();

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.PERSONS &&
        p.tipoPermiso === "W",
    ),
  );
  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        showButtons={hasWriteAccess}
        title="Maestro de Personas"
        loading={loadingPersons}
        backButton={
          Boolean(searchParams.get("dni")) &&
          location?.state?.from === AppRoutes.ACCOUNTS_DETAIL
        }
        onBack={handleBackButton}
        addButtonLabel="Agregar"
        handleAdd={handleAdd}
      />

      <PersonsFilter
        control={control}
        clearFilters={clearFilters}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        filtersApplied={filtersApplied}
        loadingFilters={loadingPersons}
        disableFilters={loadingPersons}
      />

      <Flex flexDir="column" paddingX="20px">
        {Boolean(!loadingPersons && !persons?.listado?.length) && <NoData />}
        {Boolean(!loadingPersons && persons?.listado?.length) && (
          <>
            <StandardTable
              selection={selection} 
              setSelection={setSelection}
              indeterminate={indeterminate}
              noSelection
              items={persons?.listado || []}
              headers={PersonHeaderElements}
              actionElements={getActionElements}
            />
            <PaginationComponent
              page={page}
              handlePageChange={handlePageChange}
              pageSize={10}
              count={persons?.totalRecords ?? 0}
            />
          </>
        )}
      </Flex>
    </Flex>
  );
};
